import { Button } from '@platform/components';
import {
  ProfileItem,
  ProfileSection,
  ProfileTabContainer,
} from '../../components/ProfileComponents';
import { ProjectSuggestions } from '@platform/client';
import { useState } from 'react';
import { SuggestProjectModal } from './SuggestProjectModal';
import { ProjectSuggestion } from '@platform/client/schemas';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { GenerateNewSuggestionsModal } from './NewSuggestionsModal';

export const ProjectSuggestionTabContent = ({ organizationId }: { organizationId: number }) => {
  const [suggestProjectData, setSuggestProjectData] = useState<{
    id: number;
    index: number;
  }>();
  const [confirmNewSuggestionsModalOpen, setConfirmNewSuggestionsModalOpen] = useState(false);
  const {
    data: projectSuggestionData,
    isLoading,
    isError,
    refetch,
  } = ProjectSuggestions.useGetProjectSuggestions(organizationId);
  const generateNewSuggestions = ProjectSuggestions.useGenerateOrgProjectSuggestions({
    mutation: {
      onSuccess: () => refetch(),
    },
  });
  const hasExistingSuggestions =
    projectSuggestionData?.projectSuggestions?.length &&
    projectSuggestionData.projectSuggestions.length > 0;
  return (
    <ProfileTabContainer>
      <div className='flex gap-2 items-baseline'>
        {!hasExistingSuggestions && (
          <Button
            variant='secondary-color'
            size='sm'
            isDisabled={isLoading || generateNewSuggestions.isPending}
            onPress={() => generateNewSuggestions.mutate({ organizationId })}
          >
            Generate suggested projects
          </Button>
        )}
        {!!hasExistingSuggestions && (
          <Button
            variant='primary'
            size='sm'
            onPress={() => setConfirmNewSuggestionsModalOpen(true)}
            isDisabled={isLoading || generateNewSuggestions.isPending}
          >
            Generate new suggestions
          </Button>
        )}
        {(isLoading || generateNewSuggestions.isPending) && (
          <p>Loading suggestions... this may take a few minutes</p>
        )}
        {(isError || generateNewSuggestions.isError) && (
          <p className='text-red-500'>Error loading suggestions. Please try again.</p>
        )}
      </div>
      <div className='mt-4'>
        {hasExistingSuggestions ?
          <div className=''>
            {projectSuggestionData.projectSuggestions.map((suggestion, index) => (
              <ProjectSuggestionSection
                key={index}
                index={index}
                setSuggestProjectData={setSuggestProjectData}
                suggestedProject={suggestion}
              />
            ))}
          </div>
        : <p>No project suggestions generated.</p>}
      </div>
      {suggestProjectData && (
        <SuggestProjectModal
          organizationId={organizationId}
          projectSuggestionId={suggestProjectData.id}
          projectSuggestionIndex={suggestProjectData.index}
          onClose={() => setSuggestProjectData(undefined)}
          isOpen={!!suggestProjectData}
        />
      )}
      <GenerateNewSuggestionsModal
        organizationId={organizationId}
        generateNewSuggestions={generateNewSuggestions}
        isOpen={confirmNewSuggestionsModalOpen}
        onClose={() => setConfirmNewSuggestionsModalOpen(false)}
      />
    </ProfileTabContainer>
  );
};

const ProjectSuggestionSection = ({
  index,
  setSuggestProjectData,
  suggestedProject,
}: {
  index: number;
  setSuggestProjectData: React.Dispatch<
    React.SetStateAction<{ id: number; index: number } | undefined>
  >;
  suggestedProject: ProjectSuggestion;
}) => {
  return (
    <ProfileSection key={index} columns={1}>
      <div className='flex items-baseline justify-between mb-2'>
        <h3 className='text-lg font-semibold'>Suggestion #{index + 1}</h3>
        <div className='flex gap-2 items-baseline'>
          {suggestedProject.suggestedDate && (
            <p className='text-xs text-gray-400'>
              (Emailed {formatDateStr(suggestedProject.suggestedDate)})
            </p>
          )}
          <Button
            size='sm'
            variant='secondary-gray'
            onClick={() => setSuggestProjectData({ id: suggestedProject.id, index })}
          >
            {suggestedProject.suggestedDate ? 'Resend' : 'Email'} project suggestion
          </Button>
        </div>
      </div>
      <ProfileItem label='Title' value={suggestedProject.title} />
      <ProfileItem
        label='Deliverables'
        value={<DeliverablesList deliverables={suggestedProject.deliverables} />}
      />
      <ProfileItem label='Skills' value={suggestedProject.skills.join(', ')} />
      <ProfileItem label='Background' value={suggestedProject.background} />
      <ProfileItem label='Learning Outcomes' value={suggestedProject.learningOutcomes} />
      <ProfileItem label='Focus' value={suggestedProject.focus} />
    </ProfileSection>
  );
};

const DeliverablesList = ({ deliverables }: { deliverables: string[] }) => {
  return (
    <ul className='list-disc pl-3'>
      {deliverables.map((deliverable, index) => (
        <li className='text-gray-700 font-medium text-sm' key={index}>
          {deliverable}
        </li>
      ))}
    </ul>
  );
};
